import { BaseApiService, API_CONFIG } from './base.service';

/**
 * Service for managing the complete contract-to-invoice-to-payment workflow
 */
export class ContractInvoicePaymentService extends BaseApiService {
  
  /**
   * Create a new invoice from a contract
   * @param contractId The contract ID to create invoice from
   * @param isDraft Whether to create as draft (default: true)
   * @param notes Optional invoice notes
   * @returns Promise with invoice creation result
   */
  async createInvoiceFromContract(
    contractId: string,
    isDraft: boolean = true,
    notes: string = "Invoice created via API"
  ): Promise<any> {
    return this.post(`${API_CONFIG.ENDPOINTS.CONTRACTS}/${contractId}/create-invoice`, {
      is_draft: isDraft,
      notes: notes
    });
  }
  
  /**
   * Get draft invoice details for review
   * @param invoiceId The invoice ID to review
   * @returns Promise with invoice review details
   */
  async reviewDraftInvoice(invoiceId: string): Promise<any> {
    return this.get(`/api/v1/contract-invoice-payment/invoices/${invoiceId}/review`);
  }
  
  /**
   * Post (finalize) a draft invoice
   * @param invoiceId The invoice ID to post
   * @returns Promise with updated invoice details
   */
  async postInvoice(invoiceId: string): Promise<any> {
    return this.post(`/api/v1/contract-invoice-payment/invoices/${invoiceId}/post`, {});
  }
  
  /**
   * Get available payment methods for a customer from Stripe
   * @param customerId The customer ID
   * @returns Promise with payment methods and Stripe customer details
   */
  async getCustomerPaymentMethods(customerId: number): Promise<any> {
    return this.get(`/api/v1/contract-invoice-payment/customers/${customerId}/payment-methods`);
  }
  
  /**
   * Create a Stripe payment intent for an invoice
   * @param invoiceId The invoice ID
   * @param paymentMethodId Optional specific payment method ID to use
   * @param savePaymentMethod Whether to save the payment method for future use
   * @returns Promise with payment intent details
   */
  async createPaymentIntent(
    invoiceId: string,
    paymentMethodId?: string,
    savePaymentMethod: boolean = false
  ): Promise<any> {
    return this.post(`/api/v1/contract-invoice-payment/invoices/${invoiceId}/create-payment-intent`, {
      payment_method_id: paymentMethodId,
      save_payment_method: savePaymentMethod
    });
  }
  
  /**
   * Get the current status of payment intent for an invoice
   * @param invoiceId The invoice ID
   * @returns Promise with payment intent status details
   */
  async getPaymentIntentStatus(invoiceId: string): Promise<any> {
    return this.get(`/api/v1/contract-invoice-payment/invoices/${invoiceId}/payment-intent-status`);
  }
  
  /**
   * Get the complete status of the contract-invoice-payment workflow
   * @param invoiceId The invoice ID
   * @returns Promise with complete workflow status
   */
  async getCompleteWorkflowStatus(invoiceId: string): Promise<any> {
    return this.get(`/api/v1/contract-invoice-payment/invoices/${invoiceId}/workflow-status`);
  }
  
  /**
   * Get a simplified workflow status for display in UI
   * @param invoiceId The invoice ID
   * @returns Promise with simplified workflow status
   */
  async getWorkflowStatusForDisplay(invoiceId: string): Promise<{
    currentStep: string;
    nextSteps: string[];
    status: 'draft' | 'finalized' | 'paid' | 'error';
    paymentIntentId?: string;
    paymentStatus?: string;
    error?: string;
  }> {
    try {
      const result = await this.getCompleteWorkflowStatus(invoiceId);
      
      // Determine current step based on invoice and payment status
      let currentStep = 'contract_created';
      let status: 'draft' | 'finalized' | 'paid' | 'error' = 'draft';
      
      if (result.workflow_status.invoice.status === 'finalized') {
        currentStep = 'invoice_finalized';
        status = 'finalized';
        
        if (result.workflow_status.payment.status === 'paid') {
          currentStep = 'payment_completed';
          status = 'paid';
        } else if (result.workflow_status.payment.has_payment_intent) {
          currentStep = 'payment_intent_created';
        }
      } else if (result.workflow_status.invoice.status === 'draft') {
        currentStep = 'invoice_created';
      }
      
      return {
        currentStep,
        nextSteps: result.next_steps || [],
        status,
        paymentIntentId: result.workflow_status.payment.payment_intent_id,
        paymentStatus: result.workflow_status.payment.status
      };
      
    } catch (error) {
      console.error('Error getting workflow status:', error);
      return {
        currentStep: 'error',
        nextSteps: [],
        status: 'error',
        error: 'Failed to get workflow status'
      };
    }
  }

  /**
   * Get Stripe payment status events for a specific payment intent
   * @param paymentIntentId The Stripe PaymentIntent ID
   * @returns Promise with list of payment status events
   */
  async getPaymentIntentEvents(paymentIntentId: string): Promise<any> {
    return this.get(`/api/v1/stripe-billing/payment-intents/${paymentIntentId}/stripe-events`);
  }

  /**
   * Get Stripe payment status for an invoice
   * @param invoiceId The invoice ID
   * @returns Promise with list of payment status records
   */
  async getInvoiceStripePaymentStatus(invoiceId: string): Promise<any> {
    return this.get(`/api/v1/stripe-billing/invoices/${invoiceId}/stripe-payment-status`);
  }
}

/**
 * Global instance of the contract invoice payment service
 */
export const contractInvoicePaymentService = new ContractInvoicePaymentService();
